import type { BillingStatus } from '../../context/AuthContext';

const DAY_S = 24 * 60 * 60;
/** EU withdrawal period (BIL-L23): 14 days from the day the contract is concluded. */
export const WITHDRAWAL_PERIOD_DAYS = 14;
/** Every card-backed trial lasts 3 days (BIL-L14 / BIL-R16). */
const TRIAL_DAYS = 3;

/**
 * Unix time (seconds) until which Profile offers "Withdraw from contract here", or null.
 *
 * The backend decides via `withdrawal_available_until` (EUR consumer, within 14 days
 * of contract conclusion). When a status payload predates that field, a trialing
 * subscription is still recognisable: the trial started 3 days before
 * `current_period_end`, so the window is derived from that and the visitor's
 * pricing currency. The backend re-checks eligibility on withdrawal either way.
 */
export function withdrawalAvailableUntil(
  status: BillingStatus | null | undefined,
  pricingCurrency: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (!status) return null;
  const now = Math.floor(nowMs / 1000);
  if (status.withdrawal_available_until !== undefined) {
    const until = status.withdrawal_available_until;
    return typeof until === 'number' && until > now ? until : null;
  }
  if ((status.subscription_status || '').toLowerCase() !== 'trialing') return null;
  if ((pricingCurrency || '').toUpperCase() !== 'EUR') return null;
  const periodEnd = status.current_period_end;
  if (typeof periodEnd !== 'number' || periodEnd <= 0) return null;
  const started = periodEnd - TRIAL_DAYS * DAY_S;
  // The day of conclusion does not count: the period ends at the end of the 14th day after it.
  const deadline = Math.floor(started / DAY_S) * DAY_S + (WITHDRAWAL_PERIOD_DAYS + 1) * DAY_S;
  return now < deadline ? deadline : null;
}

/** Whether the status needs the pricing currency to decide (no server-side answer). */
export function withdrawalNeedsPricingCurrency(status: BillingStatus | null | undefined): boolean {
  return Boolean(
    status
      && status.withdrawal_available_until === undefined
      && (status.subscription_status || '').toLowerCase() === 'trialing',
  );
}
